// The always theme's page-level look for Always AI's own plain elements
// (NO-792): the roster at /rds and the RD page at /rds/$rdId. Every class here
// is a `brand:` utility, so it applies only under `data-theme="always"` and
// the classic look is untouched. These go on plain elements and router links
// only, never on an @ndd/ui component that takes a className (a brand utility
// would beat the caller's className there; those use the theme-only-value
// pattern, see apps/always-ai/AGENTS.md). The values they read (the eyebrow,
// quiet-link, press and utility values, and --surface-line) are defined only
// in the theme blocks of @ndd/ui's and Always AI's styles.css.
// It holds the reusable looks only: one-off page specifics (a page's max width,
// the roster's rollup grid, a single bold title) stay inline beside their
// classic classes, and they too must be `brand:` utilities.
// The approved spec is the NO-791 and NO-792 Linear comments "Approved design
// spec (Justin, 2026-10-22)". No imports, so `tsx --test` modules can use it.

/** A white card on cream: a 1px plum line at 14%, the 22px radius, no drop shadow. */
export const THEMED_CARD =
  "brand:rounded-xl brand:border-(color:--surface-line) brand:shadow-none";

/** A card's beige header band becomes card-white over a hairline. */
export const THEMED_BAND = "brand:bg-card brand:border-(color:--surface-line)";

/** A hairline rule inside a card (row borders, a tab row's baseline). */
export const THEMED_RULE = "brand:border-(color:--surface-line)";

/** Hairline rules between a card's children (`divide-*`). */
export const THEMED_DIVIDE = "brand:divide-(color:--surface-line)";

/** A page title: 32px display. */
export const THEMED_PAGE_TITLE =
  "brand:text-display brand:leading-[1.15] brand:font-bold";

/** A card title in display type: sentence case, 17px, bold. */
export const THEMED_CARD_TITLE =
  "brand:font-sans brand:text-title-sm brand:font-bold brand:normal-case brand:tracking-normal brand:text-foreground";

/**
 * The mono eyebrow (11.5px mono caps) for a label that keeps its own classic
 * look, so it can't be an Eyebrow; it reads the same theme values Eyebrow does.
 */
export const THEMED_EYEBROW =
  "brand:font-(family-name:--eyebrow-font) brand:font-(weight:--eyebrow-weight) brand:text-(length:--eyebrow-size) brand:tracking-(--eyebrow-tracking) brand:text-(color:--eyebrow-ink)";

/** A value set in display type (a figure or short fact in a card). */
export const THEMED_DISPLAY_VALUE = "brand:font-sans";

/**
 * The quiet inline link: always underlined, 1px at a 3px offset, semibold. It
 * reads the values Button's quiet-link variant reads.
 */
export const THEMED_QUIET_LINK =
  "brand:[text-decoration-line:var(--link-quiet-line)] brand:decoration-(length:--link-quiet-thickness) brand:underline-offset-(--link-quiet-offset) brand:font-(weight:--link-quiet-weight)";

/**
 * The primary action as an olive pill with a 3px press edge, for a router
 * link that can't be a Button. It reads the values Button's press variant
 * reads; the hover and active states are paired with their brand forms.
 */
export const THEMED_PRESS =
  "brand:h-[38px] brand:rounded-(--button-press-radius) brand:border-0 brand:bg-primary brand:px-[18px] brand:text-body-sm brand:font-(weight:--button-press-weight) brand:text-primary-foreground brand:shadow-(--shadow-press-edge) brand:hover:bg-primary-hover brand:active:translate-y-(--button-press-travel) brand:active:shadow-(--shadow-press-active)";

/**
 * A utility button: card-white, a 1px line, the 12px radius and no shadow; an
 * olive inset while its menu is open. It reads Button's utility values.
 */
export const THEMED_UTILITY =
  "brand:rounded-md brand:border-(color:--button-utility-border) brand:bg-(--button-utility-bg) brand:shadow-(--button-utility-shadow) brand:font-(weight:--button-utility-weight) brand:aria-expanded:border-(color:--button-selected-border) brand:aria-expanded:shadow-(--button-selected)";

/** A list row's hover: cream rather than the muted fill. */
export const THEMED_ROW_HOVER = "brand:hover:bg-background";
